import * as yup from 'yup'
import YupPassword from 'yup-password'
YupPassword(yup)

import {
  makeReqStringSchema,
  makePasswordSchema,
  makeReqEmailSchema,
} from '@/base'
import { TosVersion, BaaVersion } from '@/constants/businessConstants'

/** Values must match the API's `SignupFlow` enum — they are sent verbatim. */
export enum SignupFlow {
  standard = 'standard',
  practiceReadiness = 'practice_readiness',
}

/** Rules to compose, not a schema to share whole — screens add their own. */
export const sharedSignupFieldRules = {
  firstName: makeReqStringSchema({ field: 'first name' }),
  lastName: makeReqStringSchema({ field: 'last name' }),
  email: makeReqEmailSchema({ field: 'email' }),
  password: makePasswordSchema(),
  confirmPassword: makeReqStringSchema({ field: 'confirm password' }).test(
    'Password Test',
    'Passwords do not match',
    (confirmPassword, context) => {
      const password = context.parent['password']
      return password === confirmPassword
    }
  ),
  termsOfService: yup
    .boolean()
    .oneOf([true], 'You must agree to the terms of service'),
}

export interface SignupFormValues {
  firstName: string
  lastName: string
  email: string
  password: string
  phoneNumber?: string
  state?: string
  smsConsent?: boolean
  smsContactPhoneNumber?: string
}

/**
 * Required here so the compiler catches a renamed or dropped field. Left
 * optional, a screen could silently stop sending one and the builder would just
 * omit it.
 */
export interface AboutYouFormValues extends SignupFormValues {
  phoneNumber: string
  state: string
  smsConsent: boolean
  smsContactPhoneNumber: string
  confirmPassword: string
  sameAsBusinessPhoneNumber: boolean
  freeTrialPromoCode: string | null
  termsOfService: boolean
}

export interface PracticeReadinessAccountFormValues extends SignupFormValues {
  confirmPassword: string
  termsOfService: boolean
}

/** The single place the current ToS and BAA versions are stamped. */
export const buildSignupPayload = ({
  values,
  searchParams,
  signupFlow = SignupFlow.standard,
}: {
  values: SignupFormValues
  searchParams?: URLSearchParams
  /** A constant from the screen — never read off the URL. */
  signupFlow?: SignupFlow
}) => ({
  firstName: values.firstName,
  lastName: values.lastName,
  email: values.email,
  password: values.password,
  ...(values.phoneNumber ? { phoneNumber: values.phoneNumber } : {}),
  ...(values.state ? { state: values.state } : {}),
  // `|| null`, not the raw getter: `?trial=` yields '' and must send null.
  freeTrialPromoCode: searchParams?.get('trial') || null,
  smsContactPhoneNumber: values.smsConsent
    ? values.smsContactPhoneNumber
    : undefined,
  hadConsult: searchParams?.get('consult') === 'true',
  tosVersion: TosVersion.tos_01_2024,
  tosAcceptedOn: Date.now(),
  baaVersion: BaaVersion.baa_07_2025,
  signupFlow,
})
